import { useAccount, useMsal } from "@azure/msal-react";
import {
    ActionIcon,
    AppShell,
    AppShellMain,
    Box,
    Button,
    Center,
    Container,
    Flex,
    Group,
    Notification,
    rem,
    ScrollArea,
    Text,
    TextInput,
    Title,
    Tooltip,
    useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
    IconExclamationCircle,
    IconLayoutSidebarLeftCollapse,
    IconLayoutSidebarLeftExpand,
    IconLogout,
    IconMessage2Plus,
    IconSearch,
    IconSettings,
    IconX,
} from "@tabler/icons-react";
import { Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useLiveQuery } from "dexie-react-hooks";
import { useFeature } from "flagged";
import { useEffect, useMemo, useState } from "react";
import { useTour } from "src/contexts/tourContext";
import { useApiHealth } from "src/hooks/useApiHealth";
import { getShortcutText } from "src/hooks/useKeyboardShortcuts";
import { useMetadata } from "src/hooks/useMetadata";
import { useNotifications } from "src/hooks/useNotifications";
import { useSyncUserRoles } from "src/hooks/useSyncUserRoles";
import { useNotAuthorizedUser } from "src/utils/useAdmin";
import { SiemensLogo } from "../assets/SiemensLogo";
import { db } from "../db";
import { useLoadChatHistory } from "../hooks/useLoadChatHistory";
import { loginRequest } from "../utils/authConfig";
import { getFilterDisplayText } from "../utils/filterDisplayUtils";
import { Chats } from "./Chats";
import { GuidedTour } from "./GuidedTour";
import { HeaderNav } from "./HeaderNav";
import { LoadingScreen } from "./LoadingScreen";
import { mainTourSteps } from "./mainTourSteps";
import { SettingsModal } from "./SettingsModal";
import { TermsOfUseModal, useTermsOfUse } from "./TermsOfUseModal";

const APP_TITLE = import.meta.env.VITE_APP_TITLE ?? "Knowledge Search";

interface ToDesktop {
    send: (channel: string, ...args: unknown[]) => void;
    receive: (channel: string, func: (...args: unknown[]) => void) => void;
}

interface AppNotification {
    id: string;
    level: string;
    content: string;
    timestamp: number;
}

declare global {
    interface Window {
        todesktop?: ToDesktop;
    }
}

export function Layout({ withSidebar = true }) {
    const location = useLocation();
    const sentryUserFeedbackEnabled = useFeature("SENTRY_USER_FEEDBACK");
    const theme = useMantineTheme();
    const { resetChatMetadata } = useMetadata();
    const [isChatPage, setIsChatPage] = useState(false);

    const [isAppReady, setIsAppReady] = useState(false);
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const { isTourOpen, completeTour } = useTour();
    const [acquireToken, setAcquireToken] = useLocalStorage("acquireToken", false);
    const [preferenceDisplay, setPreferenceDisplay] = useState("My Preferences");
    const { data: apiHealth } = useApiHealth();

    // Check if we're in widget fullscreen mode
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.split("?")[1] || "");
    const isWidgetFullscreen =
        urlParams.get("widgetFullscreen") === "true" ||
        hashParams.get("widgetFullscreen") === "true";
    const showHeader = !isWidgetFullscreen;

    const { isForbidden } = useSyncUserRoles();
    const { instance, inProgress } = useMsal();
    const account = useAccount();
    const { termsModalOpened, closeTermsModal } = useTermsOfUse();

    const isSmallerThanSm = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
    const isSmallerThanMd = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);

    // Sidebar follows screen size: hidden on mobile, shown on desktop
    const [hideHistoryBar, setHideHistoryBar] = useState(false);

    // Sync sidebar state with screen size on mount and resize
    useEffect(() => {
        setHideHistoryBar(isSmallerThanMd);
    }, [isSmallerThanMd]);

    const getNotificationColor = (severity: string) => {
        switch (severity) {
            case "error":
                return theme.colors.red[6];
            case "warn":
                return theme.colors.yellow[6];
            case "info":
                return theme.colors.blue[6];
            default:
                return theme.colors.dark[6];
        }
    };

    // Load chat history from backend (only on root page for main app)
    useLoadChatHistory({ enabled: location.pathname === "/" });

    const chats = useLiveQuery(() => db.chats.orderBy("createdAt").reverse().toArray());

    const isNotAuthorized = useNotAuthorizedUser();
    const { data: systemNotifications } = useNotifications();
    const [visibleNotifications, setVisibleNotifications] = useState<Array<AppNotification>>([]);

    useEffect(() => {
        if (acquireToken) {
            setAcquireToken(false);
            instance.loginRedirect(loginRequest());
        }
    }, [acquireToken, instance, setAcquireToken]);

    useEffect(() => {
        if (!Array.isArray(systemNotifications) || systemNotifications.length === 0) return;

        // Update to add timestamp to notifications
        const updatedNotifications = systemNotifications.map((notification) => ({
            ...notification,
            timestamp: Date.now(),
        }));

        const dismissedNotifications = JSON.parse(
            localStorage.getItem("dismissedNotifications") || "[]",
        );
        const filteredNotifications = updatedNotifications.filter(
            (notification) => !dismissedNotifications.includes(notification.id),
        );
        setVisibleNotifications(filteredNotifications);
    }, [systemNotifications]);

    const [chatPreferencesData] = useLocalStorage<{
        selectedProductLines: string[];
        selectedDatasources: string[];
    }>("chatPreferences", {
        selectedProductLines: [],
        selectedDatasources: [],
    });

    useEffect(() => {
        const displayText = getFilterDisplayText(chatPreferencesData);
        setPreferenceDisplay(displayText);
    }, [chatPreferencesData]);

    const handleDismissNotification = (id: string) => {
        const stored = localStorage.getItem("dismissedNotifications") || "[]";
        const dismissedNotifications = JSON.parse(stored);
        dismissedNotifications.push(id);
        localStorage.setItem("dismissedNotifications", JSON.stringify(dismissedNotifications));
        setVisibleNotifications((prev) => prev.filter((n) => n.id !== id));
    };

    // Check if we're in widget mode
    const isWidgetMode =
        urlParams.get("mode") === "widget" || window.location.hash.includes("/widget/");

    useEffect(() => {
        // Skip authentication in widget mode (widget handles its own auth)
        if (isWidgetMode) {
            return;
        }

        // If authenticated but not authorized, redirect to unauthorized page
        if (account && isNotAuthorized) {
            navigate({ to: "/unauthorized" });
            return;
        }

        // Check if MSAL has cached accounts (user was previously logged in)
        // The useAccount() hook might not have updated yet, but MSAL might have cached accounts
        const allAccounts = instance?.getAllAccounts() || [];
        if (allAccounts.length > 0) {
            // User has cached credentials, wait for the hook to update
            // Don't trigger a redirect
            return;
        }

        // If not authenticated and no interaction in progress, trigger login
        if (!account && inProgress === "none") {
            instance.loginRedirect(loginRequest()).catch((error) => {
                console.error("Login redirect error:", error);
            });
        }
    }, [inProgress, account, instance, isWidgetMode, isNotAuthorized, navigate]);

    // Show loading screen while authentication is in progress or user is not yet authenticated
    // IMPORTANT: This must be returned AFTER all hooks are called
    // We cannot return early before hooks (like useFeature, useMantineTheme, etc.) are called
    const isLoading = inProgress !== "none" || (!account && !isWidgetMode);

    const handleLogoutRedirect = () => {
        sessionStorage.clear();
        instance.logoutRedirect({
            postLogoutRedirectUri: window.location.origin,
        });
    };

    useEffect(() => {
        setTimeout(() => {
            setIsAppReady(true);
        }, 100);
    }, []);

    const chatPagePatterns = useMemo(() => {
        const BASE_URL = window.location.origin;
        return {
            baseUrl: BASE_URL,
            chatPageRegex: new RegExp(`^${BASE_URL}/#/chats/[a-zA-Z0-9-]+$`),
            basePageRegex: new RegExp(`^${BASE_URL}(/#/?|/?)$`),
        };
    }, []);

    useEffect(() => {
        const { baseUrl, chatPageRegex, basePageRegex } = chatPagePatterns;
        const hash = (location.hash || window.location.hash).split("?")[0];
        const fullUrl = `${baseUrl}/${hash.startsWith("#") ? hash : `#${hash}`}`;
        const isChatMatch = chatPageRegex.test(fullUrl);
        const isBaseMatch = basePageRegex.test(fullUrl);

        setIsChatPage(isChatMatch || isBaseMatch);
    }, [location, chatPagePatterns]);

    if (isLoading) {
        return <LoadingScreen />;
    }

    return (
        <AppShell
            layout="default"
            padding={0}
            navbar={{
                width: { base: 200, lg: 300 },
                breakpoint: "md",
                collapsed: {
                    mobile: !isChatPage || hideHistoryBar,
                    desktop: !isChatPage || hideHistoryBar,
                },
            }}
            header={{
                height: rem(80),
            }}
        >
            {showHeader ? (
                <AppShell.Header
                    style={{
                        backgroundColor: theme.white,
                        padding: `0 ${rem(16)}`,
                        borderBottom: `1px solid ${theme.colors.gray[1]}`,
                        [`@media (minWidth: ${theme.breakpoints.md})`]: {
                            padding: `0 ${rem(30)}`,
                        },
                    }}
                >
                    <Flex align="center" h="100%" justify="space-between">
                        <ActionIcon
                            variant="subtle"
                            onClick={() => setHideHistoryBar(!hideHistoryBar)}
                            size="md"
                            color="gray.6"
                            aria-label={hideHistoryBar ? "Expand sidebar" : "Collapse sidebar"}
                            style={{ marginRight: 16 }}
                        >
                            {hideHistoryBar ? (
                                <IconLayoutSidebarLeftExpand size={20} />
                            ) : (
                                <IconLayoutSidebarLeftCollapse size={20} />
                            )}
                        </ActionIcon>
                        <Flex align="center" gap="xs">
                            <Link to="/" style={{ textDecoration: "none" }}>
                                <SiemensLogo />
                            </Link>
                            {!isSmallerThanSm && (
                                <Link to="/" style={{ textDecoration: "none" }}>
                                    <Title order={4} c="purple.8" ml="xl" lh={2}>
                                        {APP_TITLE}
                                    </Title>
                                </Link>
                            )}
                        </Flex>
                        {!isSmallerThanMd && (
                            <Container
                                style={{
                                    position: "absolute",
                                    left: "47.5%",
                                    transform: "translateX(-50%)",
                                    width: "50%",
                                }}
                            >
                                {/* API Health Check Notification */}
                                {(apiHealth?.isHealthy === false || isForbidden) && (
                                    <Notification
                                        color="red"
                                        icon={<IconExclamationCircle size="1.3rem" />}
                                        withCloseButton={false}
                                        styles={(theme) => ({
                                            root: {
                                                backgroundColor: theme.colors.red[6],
                                                borderColor: theme.colors.red[6],
                                                marginBottom: theme.spacing.xs,
                                            },
                                            description: {
                                                color: "white",
                                                fontWeight: 700,
                                            },
                                            icon: {
                                                color: "white",
                                            },
                                            closeButton: {
                                                color: "white",
                                                "&:hover": {
                                                    backgroundColor: "transparent",
                                                },
                                            },
                                        })}
                                    >
                                        System is unreachable. Please make sure you have
                                        connectivity to Siemens Energy network. If problem persists
                                        contact KS administrators.
                                    </Notification>
                                )}

                                {visibleNotifications?.map((notification) => (
                                    <Notification
                                        key={`${notification.id || notification.content}-${notification.timestamp || Date.now()}`}
                                        color={
                                            notification.level === "error"
                                                ? "red"
                                                : notification.level === "warn"
                                                  ? "yellow"
                                                  : "blue"
                                        }
                                        icon={<IconExclamationCircle size="1.3rem" />}
                                        onClose={() => handleDismissNotification(notification.id)}
                                        style={{
                                            display:
                                                visibleNotifications.indexOf(notification) === 0
                                                    ? "flex"
                                                    : "none",
                                        }}
                                        styles={(theme) => ({
                                            root: {
                                                backgroundColor: getNotificationColor(
                                                    notification.level,
                                                ),
                                                borderColor: getNotificationColor(
                                                    notification.level,
                                                ),
                                                borderWidth: 1,
                                                borderStyle: "solid",
                                            },
                                            description: {
                                                color: "white",
                                                fontWeight: 700,
                                            },
                                            icon: {
                                                marginRight: theme.spacing.sm,
                                                color: "white",
                                            },
                                            closeButton: {
                                                color: "white",
                                                "&:hover": {
                                                    backgroundColor: "transparent",
                                                },
                                            },
                                        })}
                                    >
                                        {notification.content}
                                    </Notification>
                                ))}
                            </Container>
                        )}
                        <HeaderNav preferenceDisplay={preferenceDisplay} />
                        <Group>
                            {account && isNotAuthorized ? (
                                <Group gap="xs">
                                    <Text size="sm" c="dimmed">
                                        Logged in as: {account.username}
                                    </Text>
                                    <Button
                                        leftSection={<IconLogout size={16} />}
                                        variant="light"
                                        onClick={handleLogoutRedirect}
                                        color="red"
                                    >
                                        Logout
                                    </Button>
                                </Group>
                            ) : null}
                        </Group>
                    </Flex>
                </AppShell.Header>
            ) : undefined}

            {withSidebar && !isNotAuthorized && !hideHistoryBar ? (
                <AppShell.Navbar
                    style={{
                        height: "calc(100vh - 80px)",
                        display: "flex",
                        flexDirection: "column",
                        borderRight: `1px solid ${theme.colors.gray[1]}`,
                        backgroundColor: theme.white,
                    }}
                >
                    <Box
                        style={{
                            padding: 4,
                            marginTop: ".5rem",
                            marginBottom: ".25rem",
                        }}
                    >
                        <Button
                            disabled={isForbidden}
                            fullWidth
                            variant="filled"
                            leftSection={<IconMessage2Plus size={16} />}
                            onClick={async () => {
                                resetChatMetadata();
                                navigate({ to: "/" });
                            }}
                            data-tour="new-chat"
                        >
                            New Chat ({getShortcutText("I")})
                        </Button>
                    </Box>
                    <TextInput
                        variant="default"
                        placeholder="Search"
                        value={search}
                        onChange={(event) => setSearch(event.currentTarget.value.toLowerCase())}
                        style={{ padding: `${rem(2)} ${rem(4)}`, paddingInline: 4 }}
                        leftSection={<IconSearch opacity={0.8} size={20} />}
                        rightSection={
                            !!search && (
                                <ActionIcon aria-label="Search" onClick={() => setSearch("")}>
                                    <IconX opacity={0.5} size={20} />{" "}
                                </ActionIcon>
                            )
                        }
                        data-tour="search"
                    />
                    <Box
                        // grow
                        component={ScrollArea}
                        data-tour="chat-history"
                        style={{
                            height: "calc(100vh - 240px)",
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            "& .mantineScrollAreaViewport": {
                                display: "flex",
                                flexDirection: "column",
                            },
                        }}
                    >
                        {!isForbidden && <Chats chats={chats} search={search} />}
                    </Box>
                    {sentryUserFeedbackEnabled ? (
                        <Group
                            style={{
                                minHeight: "100px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                background: "white",
                            }}
                            gap="sm"
                            p="lg"
                            data-tour="settings-action"
                        >
                            <SettingsModal>
                                <Tooltip label="Settings" position="top">
                                    <ActionIcon
                                        size="lg"
                                        variant="subtle"
                                        aria-label="Settings"
                                        style={(theme) => ({
                                            borderRadius: theme.radius.md,
                                            "&:hover": {
                                                backgroundColor: theme.colors.blue[1],
                                                transform: "translateY(-1px)",
                                            },
                                        })}
                                    >
                                        <IconSettings size={20} />
                                    </ActionIcon>
                                </Tooltip>
                            </SettingsModal>
                            <Tooltip label="Logout" position="top">
                                <ActionIcon
                                    aria-label="Logout"
                                    size="lg"
                                    variant="subtle"
                                    onClick={handleLogoutRedirect}
                                    title={
                                        instance.getActiveAccount()?.username
                                            ? instance.getActiveAccount()?.username
                                            : "Unknown"
                                    }
                                    style={(theme) => ({
                                        borderRadius: theme.radius.md,
                                        "&:hover": {
                                            backgroundColor: theme.colors.red[1],
                                            transform: "translateY(-1px)",
                                        },
                                    })}
                                >
                                    <IconLogout size={20} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>
                    ) : (
                        <Center p="xs" style={{ gap: "1rem" }} data-tour="settings-action">
                            <SettingsModal>
                                <Tooltip label="Settings">
                                    <ActionIcon aria-label="Settings" style={{ flex: 1 }} size="xl">
                                        <IconSettings color="currentColor" size={20} />
                                    </ActionIcon>
                                </Tooltip>
                            </SettingsModal>
                            <Tooltip label="Logout">
                                <ActionIcon
                                    aria-label="Logout"
                                    style={{ flex: 1 }}
                                    size="xl"
                                    onClick={handleLogoutRedirect}
                                    title={
                                        instance.getActiveAccount()?.username
                                            ? instance.getActiveAccount()?.username
                                            : "Unknown"
                                    }
                                >
                                    <IconLogout color="currentColor" />
                                </ActionIcon>
                            </Tooltip>
                        </Center>
                    )}
                </AppShell.Navbar>
            ) : undefined}
            <AppShellMain>
                <Outlet />
            </AppShellMain>

            <GuidedTour
                steps={mainTourSteps}
                onComplete={completeTour}
                onClose={completeTour}
                isOpen={isTourOpen}
                pathCheck={() => {
                    return location.pathname === "/";
                }}
            />

            {account && !isNotAuthorized && isAppReady && (
                <TermsOfUseModal opened={termsModalOpened} onClose={closeTermsModal} />
            )}
        </AppShell>
    );
}
